import React from "react";
import { BAND_MULTIPLIER_DEFAULT, getBandMultiplier, subscribeBandMultiplier } from "../../utils/bollingerBandScale";
import { computeBandScenarioPnl } from "../../utils/bandScenarioPnl";

const formatNumberWithCommaAndDecimal = (number, decimalPlaces = 3) => {
    if (isNaN(number)) return "";
    return Number(number).toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });
};

const formatRatio = (n) => (Number.isFinite(n) ? `₩${formatNumberWithCommaAndDecimal(n, 1)}` : "—");

// 반올림 후 값(-0 포함)을 기준으로 부호를 판정한다 — 원본 n(예: -0.4)으로 부호를 매기면
// 반올림 결과가 0인데도 "-₩0"이 표시되는 버그가 생긴다.
const formatSignedKrw = (n) => {
    if (!Number.isFinite(n)) return "—";
    const rounded = Math.round(n);
    if (rounded === 0) return "₩0";
    const sign = rounded > 0 ? "+" : "-";
    return `${sign}₩${Math.abs(rounded).toLocaleString()}`;
};

// 손익 색 톤도 formatSignedKrw와 동일하게 반올림 값 기준으로 판정해야 표기와 색이 어긋나지 않는다.
const getPnlTone = (n) => {
    if (!Number.isFinite(n)) return "muted";
    const rounded = Math.round(n);
    if (rounded === 0) return "muted";
    return rounded > 0 ? "gain" : "loss";
};

const toneTextClass = (tone) =>
    tone === "gain" ? "text-gain" : tone === "loss" ? "text-loss" : "text-content-muted";

// 글리프(▲/▼/◆)는 방향/기준을 보강하는 장식용 표시라 aria-hidden 처리하고,
// 라벨 텍스트를 별도 자식 텍스트 노드로 두어 스크린리더/테스트 매칭에는 영향이 없게 한다.
// 글리프 자체는 손익색이 아닌 고정 muted 톤을 사용한다 — 손익은 금액 텍스트와 accent 보더로만 전달한다.
// middle(밴드 중앙 회귀)은 최상/최악 순위 판단 대상이 아닌 "참조 시나리오"이므로 showBadge:false로
// 배지를 아예 렌더하지 않는다(bestKey는 high/low 사이에서만 결정되므로 자연히 배지 후보가 아니지만,
// 의도를 명시적으로 드러내기 위해 플래그를 둔다).
const SCENARIOS = [
    { key: "high", label: "밴드 상단 도달", glyph: "▲", accentSide: "top", showBadge: true },
    { key: "middle", label: "밴드 중앙 회귀", glyph: "◆", accentSide: "middle", showBadge: false },
    { key: "low", label: "밴드 하단 도달", glyph: "▼", accentSide: "bottom", showBadge: true },
];

// 손익 부호(gain/loss/muted) × 카드 방향(top/bottom/middle) 조합의 accent 보더 클래스.
// Tailwind JIT는 소스에 리터럴로 존재하는 클래스명만 스캔하므로 템플릿 문자열로
// 동적 생성하지 않고 모든 조합을 명시적으로 나열한다.
// middle은 "참조 시나리오"라는 구조적 의미를 표현하므로 손익 tone과 무관하게 항상 동일한
// 점선(dashed) + 게이지 Mid 기준선과 동일한 warning 토큰을 사용한다(bollinger_gauge.js의
// .bb-mid가 var(--color-warning)을 쓰는 것과 동일한 시각 언어).
const MIDDLE_ACCENT_CLASS = "border-t-2 border-dashed border-t-warning";
const ACCENT_BORDER_CLASS = {
    top: {
        gain: "border-t-2 border-t-gain",
        loss: "border-t-2 border-t-loss",
        muted: "border-t-2 border-t-surface-border-light",
    },
    bottom: {
        gain: "border-b-2 border-b-gain",
        loss: "border-b-2 border-b-loss",
        muted: "border-b-2 border-b-surface-border-light",
    },
    middle: {
        gain: MIDDLE_ACCENT_CLASS,
        loss: MIDDLE_ACCENT_CLASS,
        muted: MIDDLE_ACCENT_CLASS,
    },
};

class BandScenarioPnl extends React.Component {
    constructor(props) {
        super(props);
        this.state = { multiplier: getBandMultiplier() };
        this.unsubscribeMultiplier = null;
    }

    componentDidMount() {
        // 다른 게이지 인스턴스나 다른 탭에서 바꾼 배율을 실시간 반영 (bollinger_gauge.js 와 동일 패턴)
        this.unsubscribeMultiplier = subscribeBandMultiplier((multiplier) => {
            this.setState({ multiplier });
        });
    }

    componentWillUnmount() {
        if (this.unsubscribeMultiplier) this.unsubscribeMultiplier();
    }

    render() {
        const { bollinger, exchangeRate, fiatPositions, riskyPositions, riskyMarkPrices } = this.props;
        const { multiplier } = this.state;

        const result = computeBandScenarioPnl({
            bollinger,
            multiplier,
            exchangeRate,
            fiatPositions,
            riskyPositions,
            riskyMarkPrices,
        });

        if (!result || !result.ready) {
            return null;
        }

        const isScaled = multiplier !== BAND_MULTIPLIER_DEFAULT;

        return (
            <div
                className="mt-3 rounded-xl border border-surface-border bg-surface-card p-4 sm:p-5 animate-fade-in"
                title="가정: 프리미엄 변동은 KRW측 가격에 반영되고 코인 USD 가격은 불변, BTC 비율 밴드를 전 티커 프록시로 적용합니다."
            >
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-content">밴드 도달 시 예상 손익</span>
                    {isScaled && (
                        <span className="rounded-md bg-brand-subtle px-1.5 py-0.5 font-num text-xs font-semibold text-brand-light">
                            ×{multiplier}
                        </span>
                    )}
                </div>

                {!result.hasPosition ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-4 text-center">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            aria-hidden="true"
                            className="h-8 w-8 text-content-muted"
                        >
                            <path d="M9 12h6M9 16h6M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-7-7z" />
                            <path d="M13 2v7h7" />
                        </svg>
                        <p className="text-xs text-content-muted">
                            보유 포지션이 없어 시나리오 손익을 계산할 수 없습니다
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
                            {SCENARIOS.map(({ key, label, glyph, accentSide, showBadge }) => {
                                const scenario = result[key];
                                if (!scenario) return null;
                                const isBest = showBadge && result.bestKey === key;
                                const { ratio, total, delta } = scenario;
                                const tone = getPnlTone(total);
                                const toneClass = toneTextClass(tone);
                                const deltaToneClass = toneTextClass(getPnlTone(delta));
                                // 최상/최악은 손익의 절대색(gain/loss)이 아닌 상대 순위를 나타내므로
                                // 중립 토큰(brand=최상 강조 / surface-hover=최악)으로 색 채널을 분리한다.
                                const badgeClass = isBest
                                    ? "bg-brand-subtle text-brand-light"
                                    : "bg-surface-hover text-content-secondary";
                                const badgeLabel = isBest ? "최상" : "최악";
                                const accentClass = ACCENT_BORDER_CLASS[accentSide][tone];

                                return (
                                    <div
                                        key={key}
                                        className={`min-w-0 rounded-lg bg-surface-elevated p-2.5 transition-colors sm:p-3 ${accentClass}`}
                                    >
                                        <div className="flex items-center justify-between gap-1">
                                            <span className="flex min-w-0 items-center gap-1 text-xs font-semibold text-content-secondary">
                                                <span aria-hidden="true" className="shrink-0 text-content-muted">
                                                    {glyph}
                                                </span>
                                                <span className="truncate">{label}</span>
                                            </span>
                                            {showBadge && (
                                                <span
                                                    className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold transition-colors ${badgeClass}`}
                                                >
                                                    {badgeLabel}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-2 font-num text-xs text-content-muted">
                                            시나리오 환율 {formatRatio(ratio)}
                                        </div>
                                        <div
                                            className={`mt-2 font-num text-sm font-semibold transition-colors sm:text-xl ${toneClass}`}
                                        >
                                            {formatSignedKrw(total)}
                                        </div>
                                        <div className={`mt-1 font-num text-xs transition-colors ${deltaToneClass}`}>
                                            현재 대비 {formatSignedKrw(delta)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* P1-3: title 툴팁은 터치 기기에서 전달되지 않으므로 동일 내용을 캡션으로도 노출한다.
                            "전체 보유 포지션 기준" 문구로 테이블 상단의 필터(검색/1만이하 숨김 등)와 무관함을 명시한다.
                            중앙 시나리오 언급은 한 줄 이내로 보완(후속 요청). */}
                        <p className="mt-2.5 text-xs text-content-muted sm:mt-3">
                            프리미엄 변동은 KRW측 가격에 반영되고 코인 USD 가격은 불변한다고 가정, BTC 비율 밴드를 전 티커 프록시로 적용합니다.
                            전체 보유 포지션 기준이며 테이블 필터와 무관하고, 중앙(Mid)은 최상/최악이 아닌 밴드 중심 회귀 참조 시나리오입니다.
                        </p>
                    </>
                )}
            </div>
        );
    }
}

export default BandScenarioPnl;
